import { useState, useCallback } from 'react';
import { ChatMessage, Citation, streamChat, RetrievalConfig } from '../services/chatService';
import { appConfig } from '../config/appConfig';

const WELCOME_MESSAGE: ChatMessage = {
  role: 'assistant',
  content: appConfig.display.welcomeMessage,
  isWelcome: true
};

export function useChat(apiUrl: string) {
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
  const [isLoading, setIsLoading] = useState(false);

  const sendMessage = useCallback(async (
    query: string,
    knowledgeBaseId: string,
    modelId: string,
    retrievalConfig?: RetrievalConfig,
    systemPrompt?: string
  ) => {
    const userMessage: ChatMessage = { role: 'user', content: query };
    const messageId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    let assistantContent = '';
    let citations: Citation[] = [];

    try {
      // eslint-disable-next-line no-loop-func
      for await (const chunk of streamChat(query, knowledgeBaseId, modelId, apiUrl, retrievalConfig, systemPrompt)) {
        if (chunk.type === 'citations') {
          citations = chunk.data;
        } else if (chunk.type === 'content') {
          assistantContent += chunk.data;
          setMessages(prev => {
            const newMessages = [...prev];
            const lastMsg = newMessages[newMessages.length - 1];
            if (lastMsg?.role === 'assistant') {
              lastMsg.content = assistantContent;
              lastMsg.citations = citations;
              lastMsg.messageId = messageId;
            } else {
              newMessages.push({ role: 'assistant', content: assistantContent, citations, messageId });
            }
            return newMessages;
          });
        } else if (chunk.type === 'error') {
          throw new Error(chunk.data);
        }
      }
    } catch (error: any) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Error: ${error.message}` 
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl]);

  const clearChat = useCallback(() => {
    setMessages([WELCOME_MESSAGE]);
  }, []);

  return { messages, isLoading, sendMessage, clearChat };
}
